/**
 * Competitive Comparison Component
 * Shows Finova vs. Conta Azul, Omie, Bling comparison
 * Key investor slide for Brazilian market positioning
 */
import React from 'react';
import { Check, X, Minus, Sparkles, Crown, Zap, Globe, Code } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Feature {
  name: string;
  category: string;
  finova: 'yes' | 'no' | 'partial' | 'planned';
  contaAzul: 'yes' | 'no' | 'partial';
  omie: 'yes' | 'no' | 'partial';
  bling: 'yes' | 'no' | 'partial';
  highlight?: boolean;
  description?: string;
}

const FEATURES: Feature[] = [
  // AI & Automation
  {
    name: 'Extração de NF-e com IA',
    category: 'IA & Automação',
    finova: 'yes',
    contaAzul: 'no',
    omie: 'no',
    bling: 'no',
    highlight: true,
    description: '70% dos campos extraídos automaticamente',
  },
  {
    name: 'Categorização Inteligente',
    category: 'IA & Automação',
    finova: 'yes',
    contaAzul: 'no',
    omie: 'partial',
    bling: 'no',
    highlight: true,
  },
  {
    name: 'Previsão de Fluxo de Caixa',
    category: 'IA & Automação',
    finova: 'planned',
    contaAzul: 'no',
    omie: 'no',
    bling: 'no',
  },

  // Multi-Currency
  {
    name: 'Multi-moeda Nativo (BRL + USD)',
    category: 'Multi-moeda',
    finova: 'yes',
    contaAzul: 'no',
    omie: 'no',
    bling: 'no',
    highlight: true,
    description: 'Perfeito para empresas cross-border',
  },
  {
    name: 'Conversão PTAX Automática',
    category: 'Multi-moeda',
    finova: 'yes',
    contaAzul: 'no',
    omie: 'no',
    bling: 'no',
  },
  {
    name: 'Ganho/Perda Cambial',
    category: 'Multi-moeda',
    finova: 'yes',
    contaAzul: 'no',
    omie: 'no',
    bling: 'no',
  },

  // Tax Compliance
  {
    name: 'Integração NF-e',
    category: 'Compliance Fiscal',
    finova: 'yes',
    contaAzul: 'yes',
    omie: 'yes',
    bling: 'yes',
  },
  {
    name: 'Exportação SPED',
    category: 'Compliance Fiscal',
    finova: 'yes',
    contaAzul: 'yes',
    omie: 'yes',
    bling: 'partial',
  },
  {
    name: 'Cálculo Automático ICMS/PIS/COFINS',
    category: 'Compliance Fiscal',
    finova: 'yes',
    contaAzul: 'yes',
    omie: 'yes',
    bling: 'yes',
  },

  // UX & Interface
  {
    name: 'Interface Moderna (2026)',
    category: 'Experiência',
    finova: 'yes',
    contaAzul: 'no',
    omie: 'no',
    bling: 'no',
    highlight: true,
    description: 'Design system atualizado',
  },
  {
    name: 'Tempo Real / Sem Refresh',
    category: 'Experiência',
    finova: 'yes',
    contaAzul: 'partial',
    omie: 'no',
    bling: 'no',
  },
  {
    name: 'Mobile Responsivo',
    category: 'Experiência',
    finova: 'yes',
    contaAzul: 'yes',
    omie: 'partial',
    bling: 'partial',
  },

  // API & Integration
  {
    name: 'API RESTful Completa',
    category: 'Integrações',
    finova: 'yes',
    contaAzul: 'partial',
    omie: 'yes',
    bling: 'partial',
    highlight: true,
  },
  {
    name: 'Webhooks',
    category: 'Integrações',
    finova: 'yes',
    contaAzul: 'partial',
    omie: 'yes',
    bling: 'no',
  },
  {
    name: 'Integração Bancária (Open Finance)',
    category: 'Integrações',
    finova: 'planned',
    contaAzul: 'yes',
    omie: 'yes',
    bling: 'partial',
  },
];

function StatusIcon({ status }: { status: 'yes' | 'no' | 'partial' | 'planned' }) {
  switch (status) {
    case 'yes':
      return <Check className="h-5 w-5 text-emerald-500" />;
    case 'no':
      return <X className="h-5 w-5 text-red-400" />;
    case 'partial':
      return <Minus className="h-5 w-5 text-yellow-500" />;
    case 'planned':
      return (
        <span className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
          Em breve
        </span>
      );
  }
}

function FeatureRow({ feature }: { feature: Feature }) {
  return (
    <tr className={cn(feature.highlight && 'bg-teal-50/50')}>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-start gap-2">
          {feature.highlight && <Sparkles className="h-4 w-4 text-teal-500 mt-0.5" />}
          <div>
            <span className={cn('text-gray-900', feature.highlight && 'font-medium')}>
              {feature.name}
            </span>
            {feature.description && (
              <p className="text-xs text-gray-500 mt-0.5">{feature.description}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <StatusIcon status={feature.finova} />
      </td>
      <td className="px-4 py-3 text-center">
        <StatusIcon status={feature.contaAzul} />
      </td>
      <td className="px-4 py-3 text-center">
        <StatusIcon status={feature.omie} />
      </td>
      <td className="px-4 py-3 text-center">
        <StatusIcon status={feature.bling} />
      </td>
    </tr>
  );
}

interface CompetitiveComparisonProps {
  className?: string;
}

export default function CompetitiveComparison({ className }: CompetitiveComparisonProps) {
  const categories = [...new Set(FEATURES.map((f) => f.category))];

  return (
    <div className={cn('space-y-8', className)}>
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Por que Finova?</h2>
        <p className="mt-2 text-gray-600">
          Comparação completa com as principais soluções do mercado brasileiro
        </p>
      </div>

      {/* Key Differentiators Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white">
          <Sparkles className="h-8 w-8 mb-3" />
          <h3 className="font-semibold text-lg">IA Nativa</h3>
          <p className="text-sm text-teal-100 mt-1">
            Extração automática de NF-e com 70% de precisão desde o primeiro uso
          </p>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <Globe className="h-8 w-8 mb-3" />
          <h3 className="font-semibold text-lg">Multi-moeda</h3>
          <p className="text-sm text-purple-100 mt-1">
            BRL + USD nativos. Ideal para empresas que exportam ou têm clientes no exterior
          </p>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <Zap className="h-8 w-8 mb-3" />
          <h3 className="font-semibold text-lg">Interface 2026</h3>
          <p className="text-sm text-amber-100 mt-1">
            Design moderno enquanto concorrentes mantêm interfaces de 2015
          </p>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 w-[40%]">
                  Funcionalidade
                </th>
                <th className="px-4 py-3 text-center w-[15%]">
                  <div className="flex flex-col items-center gap-1">
                    <Crown className="h-4 w-4 text-teal-500" />
                    <span className="text-sm font-semibold text-teal-600">Finova</span>
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 w-[15%]">
                  Conta Azul
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 w-[15%]">
                  Omie
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 w-[15%]">
                  Bling
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map((category) => (
                <React.Fragment key={category}>
                  <tr className="bg-gray-50/50">
                    <td
                      colSpan={5}
                      className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                    >
                      {category}
                    </td>
                  </tr>
                  {FEATURES.filter((f) => f.category === category).map((feature) => (
                    <FeatureRow key={feature.name} feature={feature} />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-emerald-500" />
          <span>Disponível</span>
        </div>
        <div className="flex items-center gap-2">
          <Minus className="h-4 w-4 text-yellow-500" />
          <span>Parcial</span>
        </div>
        <div className="flex items-center gap-2">
          <X className="h-4 w-4 text-red-400" />
          <span>Não disponível</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
            Em breve
          </span>
          <span>Planejado</span>
        </div>
      </div>

      {/* API Highlight */}
      <div className="rounded-xl border border-gray-200 p-6 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gray-900 rounded-lg">
            <Code className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">API First-Class Citizen</h3>
            <p className="text-sm text-gray-600 mt-1">
              Nossa API é completa e documentada. Cada funcionalidade disponível na interface
              também está disponível via API. Integrações com sistemas próprios, automações e
              workflows customizados.
            </p>
            <div className="mt-3 flex gap-2">
              <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                REST
              </span>
              <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                Webhooks
              </span>
              <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                OAuth 2.0
              </span>
              <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                Rate Limiting
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
